/**
 * The two plan proposals (WO-62, Anand, 12 Oct 26), as the pages read them.
 *
 *   SlotOffer       a connect or a switch-on asked for one location more than
 *                   the plan covers, and the refusal came back as an offer
 *                   with its values (supabase/functions/_shared/
 *                   gbp-agent-billing-bands.ts slotOfferFor). start_plan: the
 *                   one tap is Upgrade, the checkout for `quantity` locations
 *                   in the band. add_slot: the one tap sends the same
 *                   switch-on again with add_slot(s), and the server adds the
 *                   slot and switches the location on in one op. billing: no
 *                   one tap, Billing is the way. On the card-less trial with
 *                   its one location a start_plan offer also carries `swap`
 *                   (tester Kedar, 12 Oct 26): remove that location to switch
 *                   to another, shown under the offer with a link to its
 *                   Remove this location (TrialSwapLine).
 *   ReduceProposal  the plan pays for more locations than are in use (after a
 *                   removal or a delete), proposed until it is taken or kept
 *                   (gbp-agent-plan-proposals.ts). Its one tap is
 *                   billing_set_quantity at the cycle end; "Keep 3" is
 *                   billing_reduce_keep.
 *
 * Every sentence and every button word comes from the server, so the card,
 * the connect page, the toast and the email say the same thing. Nothing here
 * invents a value: a body that does not carry a proposal reads as none.
 *
 * Billing reads its part of the address (readBillingIntent): ?upgrade=<band>
 * &quantity=<n> starts that checkout once the page has loaded, ?enable=<id>
 * switches that location on once a plan pays for it, and ?reduce=<band>
 * picks the proposal out (the email's button). Pure, so the tests read it.
 */

export type Band = 'INR' | 'USD';

export interface SlotOffer {
  kind: 'start_plan' | 'add_slot' | 'billing';
  currency: Band;
  /** The plan's location count after the one tap. */
  quantity: number;
  /** What covers the band now: the paid quantity, or the trial's one slot. */
  covers: number;
  used: number;
  needed: number;
  sentence: string;
  /** The one tap's words ("Upgrade", "Add and connect"), or null when Billing is the way. */
  action: string | null;
  unit_minor: number;
  price_label: string;
  cycle_end: string | null;
  /**
   * On the card-less trial with its one location (tester Kedar, 12 Oct 26):
   * the other way out, a second line under the offer (gbp-agent-billing-bands.ts
   * trialSwapFor). Null or absent on every other offer.
   */
  swap?: TrialSwap | null;
}

/** Remove the one location the trial runs on to switch to another: the server's line and that location. */
export interface TrialSwap {
  sentence: string;
  location_id: string;
  name: string;
}

export interface ReduceProposal {
  currency: Band;
  quantity: number;
  used: number;
  target: number;
  cycle_end: string | null;
  sentence: string;
  /** "Reduce to 2". */
  action: string;
  /** "Keep 3". */
  keep: string;
  provider: 'dodo' | 'razorpay';
}

const isBand = (v: unknown): v is Band => v === 'INR' || v === 'USD';
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** The offer an op's body carries (enable_locations, enrol_location, settings), or null. */
export function offerOf(body: unknown): SlotOffer | null {
  const o = (body as { offer?: unknown } | null | undefined)?.offer as Partial<SlotOffer> | null | undefined;
  if (!o || typeof o !== 'object') return null;
  if (!['start_plan', 'add_slot', 'billing'].includes(String(o.kind)) || !isBand(o.currency)) return null;
  if (!isNum(o.quantity) || typeof o.sentence !== 'string' || !o.sentence) return null;
  return o as SlotOffer;
}

/** The swap an offer carries, as sent, or null when it carries none (nothing invented). */
export function swapOf(offer: SlotOffer | null | undefined): TrialSwap | null {
  const w = offer?.swap as Partial<TrialSwap> | null | undefined;
  if (!w || typeof w !== 'object') return null;
  if (typeof w.sentence !== 'string' || !w.sentence || typeof w.location_id !== 'string' || !w.location_id || typeof w.name !== 'string') return null;
  return { sentence: w.sentence, location_id: w.location_id, name: w.name };
}

/** The reduction proposals a body carries (billing_get, billing_proposals, remove_location, app_location_delete). */
export function reduceProposalsOf(body: unknown): ReduceProposal[] {
  const list = (body as { reduce_proposals?: unknown } | null | undefined)?.reduce_proposals;
  if (!Array.isArray(list)) return [];
  return list.filter(
    (p): p is ReduceProposal =>
      !!p && typeof p === 'object' && isBand((p as ReduceProposal).currency) && isNum((p as ReduceProposal).target) && typeof (p as ReduceProposal).sentence === 'string',
  );
}

/** The op body that takes a reduction: the band's plan to the proposed quantity, from the cycle end. */
export function reduceArgs(p: ReduceProposal): Record<string, unknown> {
  return { quantity: p.target, when: 'cycle_end', currency: p.currency };
}

/* ---------------------------------------------------------- the address */

export interface BillingIntent {
  /** Start the checkout in this band once Billing has loaded. */
  upgrade: Band | null;
  /** For that many locations. */
  quantity: number | null;
  /** Switch the agent on for this location once a plan pays for it. */
  enable: string | null;
  /** Pick this band's reduction proposal out. */
  reduce: Band | null;
}

export const INTENT_KEYS = ['upgrade', 'quantity', 'enable', 'reduce'] as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What Billing's address asks of it; a value it cannot act on reads as not asked. */
export function readBillingIntent(search: string): BillingIntent {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const upgrade = p.get('upgrade');
  const quantity = Number(p.get('quantity'));
  const enable = (p.get('enable') ?? '').trim();
  const reduce = p.get('reduce');
  return {
    upgrade: isBand(upgrade) ? upgrade : null,
    quantity: Number.isInteger(quantity) && quantity >= 1 && quantity <= 500 ? quantity : null,
    enable: UUID.test(enable) ? enable : null,
    reduce: isBand(reduce) ? reduce : null,
  };
}

/** The search string without the given keys ("?a=1" or ""). */
export function withoutKeys(search: string, keys: readonly string[]): string {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  for (const k of keys) p.delete(k);
  const out = p.toString();
  return out ? `?${out}` : '';
}

/**
 * Where a start_plan offer's Upgrade goes: Billing, with the checkout it
 * starts and the location to switch on once it is paid. The home's own
 * Billing tab when the page holds a home token; the app's Billing for the
 * workspace otherwise.
 */
export function upgradePath(args: { token: string | null; workspaceId: string | null; offer: Pick<SlotOffer, 'currency' | 'quantity'>; enableLocationId?: string | null }): string {
  const q = new URLSearchParams();
  if (args.token) {
    q.set('section', 'settings');
    q.set('tab', 'billing');
  } else if (args.workspaceId) q.set('ws', args.workspaceId);
  q.set('upgrade', args.offer.currency);
  q.set('quantity', String(args.offer.quantity));
  if (args.enableLocationId && UUID.test(args.enableLocationId)) q.set('enable', args.enableLocationId);
  return args.token ? `/agent/home/${args.token}?${q.toString()}` : `/app/billing?${q.toString()}`;
}

/**
 * The enrol_location body that switches a location on once the plan pays: the
 * home token's own workspace, or, signed in, the session with the location
 * (session-link.ts resolves it to the workspace that owns it).
 */
export function enrolBody(token: string | null, locationId: string): Record<string, unknown> {
  return token ? { token, op: 'enrol_location', location_id: locationId } : { op: 'enrol_location', location_id: locationId };
}
